import React, { useCallback, useRef, useState } from 'react';

import { makeStyles, createTheme } from '@material-ui/core/styles';
import { Button, CssBaseline, Link, Paper, ThemeProvider, Typography } from '@material-ui/core';

import { 
    openNewDevice, 
    openPairedDevice,
} from 'netmd-js';

const useStyles = (props: { showsList: boolean }) =>
    makeStyles(theme => ({
        layout: {
            width: 600,
            height: '100%',
            marginLeft: 'auto',
            marginRight: 'auto',

        },

        paper: {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            padding: theme.spacing(2),
            height: 600,
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(1),
            justifyContent: 'center',
            alignItems: 'center',
        },
        bottomBar: {
            display: 'flex',
            alignItems: 'center',
            marginLeft: -theme.spacing(2),
        },
        copyrightTypography: {
            textAlign: 'center',
        },
        backdrop: {
            zIndex: theme.zIndex.drawer + 1,
            color: '#fff',
        },
        textArea: {
            resize: "none",
            height: "100%",
            width: '100%',
            backgroundColor: '#424242',
            color: '#fff',
            border: "none",
            ":active": {
                border: "none"
            }
        },
        startButton: {
            width: 100,
            height: 40,
            backgroundColor: theme.palette.primary.main,
        }
    }));

const darkTheme = createTheme({
    palette: {
        type: 'dark',
        primary: {
            light: '#6ec6ff',
            main: '#2196f3',
            dark: '#0069c0',
            contrastText: '#fff',
        },
    },
});

const version = "0.1.0";

const App = () => {
    const classes = useStyles({showsList: true})();
    const textArea = useRef<any>(null);
    const [text, setText] = useState("");
    const e = useCallback(() => {
        (async () => {
            let localText = "";
            const println = (line?: string) => {
                line = line || "";
                localText += line + "\n";
                setText(localText);
            }

            const _fn = (e: any) => console.log(typeof(e) === 'string' ? e : JSON.stringify(e));

            let logger = {
                debug: _fn,
                info: _fn,
                warn: _fn,
                error: _fn,
                child: () => logger!,
            };


            println(`>>> netmd-js debugging and testing webapp ver. ${version}`);
            println(`© Asivery ${new Date().getFullYear()}`);
            println();
            println();

            if(!navigator.usb){
                println("!!! This browser doesn't support WebUSB");
                println("!!! Cannot continue");
                return;
            }

            println(">>> Awaiting device select");
            let iface = await openNewDevice(navigator.usb, logger!);
            if(iface === null){
                println("!!! Failed to open new device");
                iface = await openPairedDevice(navigator.usb, logger!);
                if(iface === null){
                    println("!!! Failed to open paired device");
                    println("!!! Cannot continue");
                    return;
                }
            }

            println("");
            println(`>>> Connected to ${iface!.netMd.getDeviceName()}`);
            println("");

            println(">>> Read device subunit identifier");
            const { netMDLevel } = await iface!._getDiscSubunitIdentifier();
            println(`--- Device of level ${netMDLevel.toString(16)}`);

            await iface.enterSecureSession();
            println(`>>> Read unit's Leaf ID`);
            const leafID = await iface.getLeafID();
            println(`--- Stringified leaf id ${leafID}`);
            println(`--- Binary leaf ID: ${Array.from(leafID).map(n => n.charCodeAt(0).toString(16).padStart(2, '0')).join('')}`);
            await iface.leaveSecureSession();
            println(`>>> Completed`);
        })();
    }, []);

    if(textArea.current !== null){
        textArea.current!.scrollTop = textArea.current!.scrollHeight;
    }

    return (
        <React.Fragment>
            <ThemeProvider theme={darkTheme}>
                <CssBaseline />

                <main className={classes.layout}>
                    <Paper className={classes.paper}>
                        {text === "" ? (
                            <Button onClick={e} className={classes.startButton}>Start</Button>
                        ) : (
                            <textarea
                                className={classes.textArea}
                                readOnly={true}
                                ref={textArea}
                                value={text}/>
                        )}
                    </Paper>
                    <Typography variant="body2" color="textSecondary" className={classes.copyrightTypography}>
                        {'© '}
                        <Link rel="noopener noreferrer" color="inherit" target="_blank" href="https://github.com/asivery/">
                            Asivery
                        </Link>{' '}
                        {new Date().getFullYear()}
                        {'.'}
                    </Typography>
                </main>
            </ThemeProvider>
        </React.Fragment>
    );
};

export default App;
